import { getApi, postApi } from "./net";

import PopupNotificationModal from "../components/PopupNotificationModal";
import React from "react";
import { createLocalization } from "../util/localization";
import { createSerializer } from "../util/flowControl";
import moment from "moment";
import { setModal } from "./modal";

export const NOTIFICATIONS_LOADING = "NOTIFICATIONS_LOADING";
export const NOTIFICATIONS_LOADED = "NOTIFICATIONS_LOADED";
export const NOTIFICATIONS_FAILED_TO_LOAD = "NOTIFICATIONS_FAILED_TO_LOAD";
export const NOTIFICATION_UPDATE_CONFIRMED_DATE = "NOTIFICATION_UPDATE_CONFIRMED_DATE";
export const NOTIFICATION_DISMISSED = "NOTIFICATION_DISMISSED";

const NOTIFICATIONS_SERIALIZER = createSerializer();

/**
 * Marks a notification as read.
 * @param {number} notificationId
 * @returns {function}
 */
export function markNotificationAsConfirmed(notificationId) {
    return async (dispatch) => {
        const date = moment().utcOffset("+02:00");

        try {
            dispatch({
                type: NOTIFICATION_UPDATE_CONFIRMED_DATE,
                notificationId,
                date
            });

            await dispatch(postApi("Notification/ConfirmWebNotificationCommand", {
                webNotificationId: notificationId
            }));
        } catch(_) {
            //
        }
    };
}

/**
 * Formatera API-anropet för att hämta notifikationer
 * @param {boolean} onlyMustRead 
 * @param {number} skip 
 * @param {number} take 
 * @returns {object}
 */
function doLoadNotifications(onlyMustRead, skip, take) {
    return getApi("Notification/WebNotificationsQuery", {
        OnlyMustRead: onlyMustRead,
        OnlyUnconfirmed: onlyMustRead,
        Skip: skip,
        Take: take
    });
}

/**
 * Loads notifications from the server.
 * @param {bool} onlyMustRead 
 * @param {number} skip 
 * @param {number} take 
 * @returns {func}
 */
export function loadNotifications(onlyMustRead = false, skip = 0, take = 10) {
    return (dispatch) => {
        return NOTIFICATIONS_SERIALIZER(async () => {
            dispatch({
                type: NOTIFICATIONS_LOADING
            });

            try {
                const {payload} = await dispatch(doLoadNotifications(onlyMustRead, skip, take));

                const notifications = Object.assign({}, ...payload.map(n => ({
                    [n.notificationId]: n
                })));

                dispatch({
                    type: NOTIFICATIONS_LOADED,
                    notifications
                });

                return payload;
            } catch (_) {
                dispatch({
                    type: NOTIFICATIONS_FAILED_TO_LOAD
                });
            }
        });
    };
}

/**
 * Laddar ner de senaste must-read-notifikationerna. Ifall det finns olästa, visa den första nedladdade som en modal.
 * @param {number} [skipUnconfirmedCount=0] Används då det rekursivt söks efter fler obekräftade notifikationer
 * @return {function}
 */
export function loadAndShowLatestMustReadNotification(skipUnconfirmedCount = 0) {
    return async (dispatch, getState) => {
        const loading = dispatch(loadNotifications());
        await loading;
        let notifications = getState().notifications.notifications;

        let unconfirmedNotification = Object.values(notifications).find(n => n.displayPopup && !n.confirmedDate && !n.dismissedOnClient);

        if (!unconfirmedNotification) {
            // If there are no unread popup notifications downloaded, download one unread popup notifications from the server
            await dispatch(loadNotifications(true, 0, 1));
            notifications = getState().notifications.notifications;
            unconfirmedNotification = Object.values(notifications).find(n => n.displayPopup && !n.confirmedDate && !n.dismissedOnClient);
        }

        if (unconfirmedNotification && !getState().modal.modal) {
            // There is no active modal and we have at least one unread popup notification. Create a modal.
            dispatch(setModal(
                React.createElement(PopupNotificationModal, {
                    notification: unconfirmedNotification,
                    localization: createLocalization(getState()),
                    deviceType: getState().view.deviceType,
                    width: getState().view.innerWidth,
                    confirm: () => {
                        // Close the modal
                        dispatch(setModal(null));

                        // If the notification is confirmed, confirm it on the server
                        dispatch(markNotificationAsConfirmed(unconfirmedNotification.notificationId));

                        // There may be more unconfirmed messages on the server - keep loading
                        dispatch(loadAndShowLatestMustReadNotification(skipUnconfirmedCount));
                    },
                    remindLater: (dismissModal) => {
                        if (dismissModal) {
                            dispatch(setModal(null));
                        }

                        // If the notification is dismissed, tag it as read on the client only
                        dispatch({
                            type: NOTIFICATION_DISMISSED,
                            notificationId: unconfirmedNotification.notificationId
                        });

                        // There may be more unconfirmed messages on the server - keep loading but offset by one since we have only confirmed locally
                        dispatch(loadAndShowLatestMustReadNotification(skipUnconfirmedCount + 1));
                    }
                })
            ));
        }
    };
}
